// src/pages/pentest/dashboard/ApiPentestAssets.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, TextField, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Button, Chip, Stack, Grid, IconButton,
  Tooltip, Collapse, LinearProgress, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useNavigate, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { prism } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Nav from './nav';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';
import { light } from '@mui/material/styles/createPalette';

/* ---- colour maps (match threat-model & exploits views) ------------------- */
const sevColor = {
  critical:'#800080', high:'#f44336', medium:'#ff9800', low:'#2196f3', info:'#4caf50'
};
const methodColor = {
  GET:'#90caf9', POST:'#a5d6a7', PUT:'#ffe082', DELETE:'#ffab91', PATCH:'#ce93d8'
};

/* ---- helpers ------------------------------------------------------------- */
const prettyMethodChip = (m) => (
  <Chip
    size="small"
    label={m}
    sx={{ bgcolor: methodColor[m] || '#e0e0e0', fontWeight: 800, color: '#000' }}
  />
);

const sevChip = (cnt, sev) =>
  (cnt ?? 0) > 0
    ? <Chip label={cnt} size="small" sx={{ bgcolor: sevColor[sev], color:'#fff', minWidth: 36 }} />
    : <Typography variant="body2" color="text.secondary">—</Typography>;

const copy = async (text='') => { try { await navigator.clipboard.writeText(text); } catch {} };

const parseJSON = (txt, fallback=[]) => {
  try {
    const v = JSON.parse(txt ?? '[]');
    return Array.isArray(v) ? v : fallback;
  } catch { return fallback; }
};

const NiceBarRow = ({ label, value = 0, max = 1, onClick }) => {
  const pct = Math.min(100, Math.round((value / Math.max(1, max)) * 100));
  return (
    <Stack spacing={0.5} sx={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }} title={label}>{label}</Typography>
        <Typography variant="caption" color="text.secondary">{value}</Typography>
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 999 }} />
    </Stack>
  );
};

/* ---- Code block with copy button for Markdown ---------------------------- */
const CodeBlock = ({ language, value }) => (
  <Box sx={{ position: 'relative' }}>
    <IconButton
      size="small"
      onClick={() => copy(value)}
      sx={{ position:'absolute', right:6, top:6, zIndex:1, bgcolor: 'rgba(0,0,0,0.04)' }}
      title="Copy"
    >
      <ContentCopyIcon fontSize="inherit" />
    </IconButton>
    <SyntaxHighlighter
      language={language || 'bash'}
      style={prism}
      wrapLines
      customStyle={{ borderRadius: 8, margin: 0, fontSize: '0.9rem' }}
    >
      {value}
    </SyntaxHighlighter>
  </Box>
);

const ApiPentestAssets = () => {
  const { pentest_id } = useParams();
  const navigate       = useNavigate();

  const [endpoints,   setEndpoints]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [searchTerm,  setSearchTerm]  = useState('');
  const [filterMethod,setFilterMethod]= useState('ALL');
  const [expanded,    setExpanded]    = useState({}); // id -> boolean

  /* ---- fetch endpoints --------------------------------------------------- */
  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) return navigate('/');
    (async () => {
      try {
        const res  = await fetch(`${BASEURL}/pentests/api/endpoints?pentest_id=${pentest_id}`, {
          headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` }
        });
        const data = await res.json();
        if (data.error) {
          if (data.message === 'Invalid token') navigate('/');
          else setError(data.message);
        } else {
          setEndpoints(Array.isArray(data.endpoints)?data.endpoints:[]);
        }
      } catch (e) {
        console.error(e);
        setError('Failed to load endpoints.');
      } finally {
        setLoading(false);
      }
    })();
  }, [pentest_id, navigate]);

  /* ---- derived stats ----------------------------------------------------- */
  const total = endpoints.length;
  const byMethod = useMemo(() => {
    const map = { GET:0, POST:0, PUT:0, DELETE:0, PATCH:0 };
    endpoints.forEach(ep => { const m=(ep.request_type||'').toUpperCase(); if (map[m]!==undefined) map[m]++; });
    return map;
  }, [endpoints]);

  const aggSev = useMemo(() => {
    const init = { critical:0, high:0, medium:0, low:0, info:0 };
    endpoints.forEach(ep => {
      init.critical += ep.critical_exploits_found || 0;
      init.high     += ep.high_exploits_found || 0;
      init.medium   += ep.medium_exploits_found || 0;
      init.low      += ep.low_exploits_found || 0;
      init.info     += ep.info_exploits_found || 0;
    });
    return init;
  }, [endpoints]);

  const topEndpoints = useMemo(() => {
    const list = endpoints
      .map(ep => ({ path: ep.path || ep.domain || '-', total: ep.total_exploits_found || 0 }))
      .sort((a,b) => b.total - a.total)
      .slice(0,5);
    const max = Math.max(1, ...list.map(x => x.total));
    return { list, max };
  }, [endpoints]);

  /* ---- filtering --------------------------------------------------------- */
  const methods = ['ALL','GET','POST','PUT','DELETE','PATCH'];
  const filtered = useMemo(() => {
    const q = (searchTerm || '').toLowerCase();
    return endpoints.filter(ep => {
      const url=(ep.path||ep.domain||'').toLowerCase();
      const meth=(ep.request_type||'').toUpperCase();
      return url.includes(q) && (filterMethod==='ALL' || meth===filterMethod);
    });
  }, [endpoints, searchTerm, filterMethod]);

  /* ---- markdown renderers ------------------------------------------------ */
  const mdComponents = {
    h1: (props) => <Typography variant="h6" sx={{ mt: 1.5, mb: 1, fontWeight: 800 }} {...props} />,
    h2: (props) => <Typography variant="subtitle1" sx={{ mt: 1.5, mb: 0.75, fontWeight: 800 }} {...props} />,
    p:  (props) => <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.7 }} {...props} />,
    li: (props) => <li style={{ marginLeft: 16 }}><Typography component="span" variant="body2" {...props} /></li>,
    code: ({ inline, className, children, ...props }) => {
      if (inline) {
        return (
          <code
            style={{
              background:'#f5f5f5', border:'1px solid #e0e0e0', borderRadius:4, padding:'1px 4px',
              fontFamily:'monospace', fontSize:'0.85em'
            }}
            {...props}
          >
            {children}
          </code>
        );
      }
      const lang = (className || '').replace('language-', '') || 'bash';
      return <CodeBlock language={lang} value={String(children).replace(/\n$/, '')} />;
    }
  };

  /* ---- UI --------------------------------------------------------------- */
  return (
    <Box sx={{ height:'100vh', display:'flex', flexDirection:'column', bgcolor:lighten(PRIMARYCOLOR, 0.98) }}>
      <Nav />

      <Box sx={{ flexGrow:1, p:{ xs:2, md:3 }, overflowY:'auto' }}>
        {/* Header */}
        <Stack direction={{ xs:'column', md:'row' }} alignItems={{ xs:'flex-start', md:'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              API Endpoints
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All discovered API routes. Expand a row to view the endpoint’s summary and example usage.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: { xs:1, md:0 } }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Method</InputLabel>
              <Select value={filterMethod} label="Method" onChange={(e)=>setFilterMethod(e.target.value)}>
                {methods.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Search path/domain…"
              value={searchTerm}
              onChange={(e)=>setSearchTerm(e.target.value)}
            />
            <Tooltip title="Reset">
              <IconButton onClick={()=>{ setSearchTerm(''); setFilterMethod('ALL'); }}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* KPI cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
              <Typography variant="overline" color="text.secondary">TOTAL ENDPOINTS</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>{total}</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={9}>
            <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display:'block', mb: 0.5 }}>
                METHODS DISTRIBUTION
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(byMethod).map(([m,c]) => (
                  <Chip key={m} label={`${m} · ${c}`} sx={{ bgcolor: methodColor[m], color:'#000', fontWeight:700 }} />
                ))}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Mini charts */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
              <Typography variant="overline" color="text.secondary">TOP 5 IMPACTED ENDPOINTS</Typography>
              <Stack spacing={1.25} sx={{ mt: 1 }}>
                {topEndpoints.list.length ? topEndpoints.list.map(r => (
                  <NiceBarRow
                    key={r.path}
                    label={r.path}
                    value={r.total}
                    max={topEndpoints.max}
                    onClick={() => setSearchTerm(r.path)}
                  />
                )) : <Typography variant="body2" color="text.secondary">No data</Typography>}
              </Stack>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ p:2, borderRadius:3, boxShadow:2 }}>
              <Typography variant="overline" color="text.secondary">AGGREGATED SEVERITIES</Typography>
              <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mt:1 }}>
                {(['critical','high','medium','low','info']).map(s => (
                  <Stack key={s} alignItems="center" spacing={0.5}>
                    <Chip size="small" label={s} sx={{ bgcolor: sevColor[s], color:'#fff', fontWeight:700 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{aggSev[s]}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Content / table */}
        {loading ? (
          <Box sx={{ display:'flex', justifyContent:'center', mt:4 }}><CircularProgress/></Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius:3, boxShadow:2 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: PRIMARYCOLOR }}>
                <TableRow>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold', width: 44 }} />
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Method</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Path</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Params</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Total</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Critical</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>High</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Medium</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Low</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }}>Info</TableCell>
                  <TableCell sx={{ color:'#fff', fontWeight:'bold' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.length ? filtered.map((ep) => {
                  const isOpen = !!expanded[ep.id];
                  const method = (ep.request_type || '-').toUpperCase();
                  const body  = parseJSON(ep.body_param);
                  const query = parseJSON(ep.query_param);
                  const pathp = parseJSON(ep.path_param);

                  return (
                    <React.Fragment key={ep.id}>
                      <TableRow hover>
                        <TableCell width={44}>
                          <IconButton size="small" onClick={() => setExpanded((s)=>({ ...s, [ep.id]: !isOpen }))}>
                            {isOpen ? <ExpandLessIcon/> : <ExpandMoreIcon/>}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ fontWeight:'bold' }}>{prettyMethodChip(method)}</TableCell>
                        <TableCell sx={{ maxWidth: 500 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{ep.path || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">{ep.domain || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            {!!body.length  && <Chip size="small" label={`body: ${body.length}`}   variant="outlined" />}
                            {!!query.length && <Chip size="small" label={`query: ${query.length}`} variant="outlined" />}
                            {!!pathp.length && <Chip size="small" label={`path: ${pathp.length}`}   variant="outlined" />}
                            {!body.length && !query.length && !pathp.length && (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell><Chip size="small" label={ep.total_exploits_found || 0} sx={{ bgcolor:'#374151', color:'#fff' }}/></TableCell>
                        <TableCell align="center">{sevChip(ep.critical_exploits_found,'critical')}</TableCell>
                        <TableCell align="center">{sevChip(ep.high_exploits_found,'high')}</TableCell>
                        <TableCell align="center">{sevChip(ep.medium_exploits_found,'medium')}</TableCell>
                        <TableCell align="center">{sevChip(ep.low_exploits_found,'low')}</TableCell>
                        <TableCell align="center">{sevChip(ep.info_exploits_found,'info')}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="See Threat Model">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/pentests/${pentest_id}/api?endpoint=${encodeURIComponent(ep.path || '')}`)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setExpanded((s)=>({ ...s, [ep.id]: !isOpen }))}
                            >
                              {isOpen ? 'Hide Summary' : 'View Summary'}
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>

                      {/* Expanded summary row */}
                      <TableRow>
                        <TableCell colSpan={11} sx={{ p: 0, bgcolor: '#fafafa' }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2 }}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                  API Summary
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  {(body.length + query.length + pathp.length) > 0 && (
                                    <Tooltip title="Copy params JSON">
                                      <IconButton
                                        size="small"
                                        onClick={() => copy(JSON.stringify({
                                          body: body, query: query, path: pathp
                                        }, null, 2))}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Stack>
                              <Divider sx={{ my: 1 }} />

                              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                                {ep.summary || '_No summary available for this endpoint._'}
                              </ReactMarkdown>

                              {/* Optional: render param lists inline */}
                              {(body.length || query.length || pathp.length) ? (
                                <Card sx={{ p: 1.5, mt: 1.5, borderRadius: 2, boxShadow: 0, border: '1px solid #eee' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Parameters</Typography>
                                  <Stack direction="row" spacing={2} flexWrap="wrap">
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Body</Typography>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {body.length ? body.map((v,i)=><Chip key={`b-${i}`} size="small" label={String(v)} />) : <Typography variant="body2">—</Typography>}
                                      </Stack>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Query</Typography>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {query.length ? query.map((v,i)=><Chip key={`q-${i}`} size="small" label={String(v)} />) : <Typography variant="body2">—</Typography>}
                                      </Stack>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Path</Typography>
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {pathp.length ? pathp.map((v,i)=><Chip key={`p-${i}`} size="small" label={String(v)} />) : <Typography variant="body2">—</Typography>}
                                      </Stack>
                                    </Box>
                                  </Stack>
                                </Card>
                              ) : null}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">No endpoints found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default ApiPentestAssets;
